import { useEffect, useRef } from 'react';
import { engine, useEngine } from '../hooks/useEngine.ts';
import { BADGES } from '../lib/storage.ts';
import { formatDuration } from '../hooks/useTicker.ts';
import { Aurora } from './Aurora.tsx';
import { InterestPicker } from './InterestPicker.tsx';
import { SparkList } from './SparkList.tsx';
import { BoltIcon, GlobeIcon, HourglassIcon, ShieldIcon, SparkleIcon } from './Icons.tsx';

export function Landing() {
  const state = useEngine();
  const previewRef = useRef<HTMLVideoElement>(null);
  const busy = state.phase === 'preparing';

  useEffect(() => {
    const video = previewRef.current;
    if (video && video.srcObject !== state.localStream) {
      video.srcObject = state.localStream;
    }
  }, [state.localStream]);

  const { player, serverStats } = state;

  return (
    <div className="relative min-h-full overflow-y-auto">
      <Aurora />

      <header className="mx-auto flex w-full max-w-6xl items-center justify-between px-5 py-6 sm:px-8">
        <a href="/" className="flex items-center gap-3" aria-label="Phidio home">
          <img src="/logo.png" alt="Phidio" className="h-8 w-auto sm:h-9" />
        </a>

        <nav className="flex items-center gap-2 text-sm">
          <a
            href="#how"
            className="hidden rounded-full px-4 py-2 text-white/60 transition hover:text-white sm:block"
          >
            How it works
          </a>
          <a
            href="#safety"
            className="hidden rounded-full px-4 py-2 text-white/60 transition hover:text-white sm:block"
          >
            Safety
          </a>
          <span className="glass flex items-center gap-2 rounded-full px-3.5 py-2 text-white/80">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-pulse-ring" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            <span className="tabular-nums font-semibold">{serverStats.online.toLocaleString()}</span>
            <span className="text-white/50">online</span>
          </span>
        </nav>
      </header>

      <main className="mx-auto flex w-full max-w-6xl flex-col items-center px-5 pb-24 sm:px-8">
        {/* Hero */}
        <section className="flex w-full flex-col items-center pt-6 text-center sm:pt-14">
          <span className="glass mb-6 rounded-full px-4 py-1.5 text-xs font-medium uppercase tracking-[0.2em] text-white/60">
            Phidio · since 2015 · rebuilt for 2026
          </span>

          <h1 className="max-w-3xl text-5xl font-extralight leading-[1.05] tracking-tight sm:text-7xl">
            Meet new people.{' '}
            <span className="bg-gradient-to-r from-phidio-400 via-phidio-500 to-phidio-700 bg-clip-text font-semibold text-transparent text-glow">
              Today.
            </span>
          </h1>

          <p className="mt-6 max-w-xl text-lg font-light text-white/60">
            One tap, one stranger, one real conversation. No feeds, no followers, no algorithm —
            just the person on the other side of the planet who's up for talking right now.
          </p>

          {/* The button */}
          <div className="relative mt-12">
            <span className="absolute inset-0 rounded-full bg-phidio-500/40 blur-2xl" aria-hidden />
            <button
              type="button"
              onClick={() => void engine.start()}
              disabled={busy}
              className="group relative isolate flex h-44 w-44 flex-col items-center justify-center rounded-full bg-gradient-to-br from-phidio-400 via-phidio-500 to-phidio-700 text-white shadow-[0_20px_70px_-15px_var(--color-phidio-500)] transition-transform duration-200 active:scale-95 disabled:cursor-wait sm:h-52 sm:w-52"
            >
              <span className="absolute inset-0 -z-10 rounded-full bg-phidio-500/60 animate-pulse-ring" />
              <span className="absolute inset-0 -z-10 rounded-full bg-phidio-500/50 animate-pulse-ring [animation-delay:-1.2s]" />
              {busy ? (
                <>
                  <span className="h-8 w-8 animate-spin rounded-full border-[3px] border-white/30 border-t-white" />
                  <span className="mt-3 text-xs uppercase tracking-[0.25em]">Starting</span>
                </>
              ) : (
                <>
                  <span className="text-2xl font-bold uppercase tracking-[0.18em] sm:text-3xl">
                    Roulette
                  </span>
                  <span className="mt-1 text-[11px] uppercase tracking-[0.25em] text-white/70">
                    tap to connect
                  </span>
                </>
              )}
            </button>
          </div>

          {state.error && (
            <p className="mt-6 max-w-md rounded-2xl border border-phidio-500/40 bg-phidio-900/30 px-5 py-3 text-sm text-phidio-100">
              {state.error}
            </p>
          )}

          <p className="mt-5 text-xs text-white/35">
            You must be 18 or older. Be kind — everyone here is a real person.
          </p>

          {/* Live preview once we hold the camera */}
          {state.localStream && (
            <div className="glass mt-8 w-56 overflow-hidden rounded-3xl p-1.5">
              <video
                ref={previewRef}
                autoPlay
                playsInline
                muted
                className={`aspect-[3/4] w-full rounded-[20px] object-cover ${
                  state.profile.mirror ? 'mirrored' : ''
                }`}
              />
              <p className="py-2 text-center text-[11px] uppercase tracking-widest text-white/40">
                camera ready
              </p>
            </div>
          )}
        </section>

        {/* Sparks you can pick back up */}
        <SparkList sparks={state.sparks} online={state.sparksOnline} />

        {/* Interests */}
        <section className="mt-16 flex w-full flex-col items-center">
          <InterestPicker interests={state.profile.interests} strict={state.profile.strict} />
        </section>

        {/* Live numbers */}
        <section className="mt-16 grid w-full grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="online now" value={serverStats.online.toLocaleString()} accent />
          <Stat label="in conversation" value={serverStats.inCall.toLocaleString()} />
          <Stat label="waiting" value={serverStats.waiting.toLocaleString()} />
          <Stat label="matches today" value={serverStats.connectionsMade.toLocaleString()} />
        </section>

        {/* Your progress */}
        {player.met > 0 && (
          <section className="mt-16 w-full">
            <h2 className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-white/40">
              Your run
            </h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="people met" value={player.met.toLocaleString()} />
              <Stat label="sparks" value={player.sparks.toLocaleString()} accent />
              <Stat label="time talking" value={formatDuration(player.seconds)} />
              <Stat label="day streak" value={`${player.streak} 🔥`} />
            </div>

            <ul className="mt-4 flex flex-wrap gap-2">
              {BADGES.map((badge) => {
                const earned = player.badges.includes(badge.id);
                return (
                  <li
                    key={badge.id}
                    title={badge.description}
                    className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-sm transition ${
                      earned
                        ? 'bg-phidio-500/20 text-phidio-100 ring-1 ring-phidio-500/40'
                        : 'bg-white/[0.04] text-white/25'
                    }`}
                  >
                    <span className={earned ? '' : 'grayscale'}>{badge.emoji}</span>
                    {badge.label}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {/* How it works */}
        <section id="how" className="mt-24 w-full scroll-mt-24">
          <h2 className="text-center text-3xl font-light sm:text-4xl">How it works</h2>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Feature
              icon={<BoltIcon />}
              title="Tap once"
              body="Allow your camera and you're in the queue. No account, no email, no profile to fill in."
            />
            <Feature
              icon={<GlobeIcon />}
              title="Get matched"
              body="Our matchmaker pairs you with someone who shares an interest — or a total wildcard if you prefer. You both land on the same ice-breaker."
            />
            <Feature
              icon={<HourglassIcon />}
              title="Stay, or skip"
              body="If it's going somewhere, both tap Stay and the conversation gets protected. If not, hit next and meet someone new instantly."
            />
            <Feature
              icon={<SparkleIcon />}
              title="Find them again"
              body="A mutual heart is a spark. It lives in your list for 12 hours, lights up when they're online, and one tap rings them back."
            />
          </div>
        </section>

        {/* Safety */}
        <section id="safety" className="glass mt-16 w-full scroll-mt-24 rounded-3xl p-8 sm:p-10">
          <div className="flex items-start gap-4">
            <span className="text-phidio-400">
              <ShieldIcon width={32} height={32} />
            </span>
            <div>
              <h2 className="text-2xl font-light">Built to be safe by default</h2>
              <ul className="mt-4 grid gap-3 text-sm text-white/60 sm:grid-cols-2">
                <li>
                  <strong className="font-semibold text-white">Peer to peer.</strong> Video and
                  audio flow straight between the two of you and are end-to-end encrypted by WebRTC.
                  Nothing is stored, ever.
                </li>
                <li>
                  <strong className="font-semibold text-white">No accounts.</strong> We never ask
                  for your name, email or phone number. Identities are throwaway and per-session.
                </li>
                <li>
                  <strong className="font-semibold text-white">One tap reporting.</strong> Report
                  ends the call immediately, and repeat offenders get removed automatically.
                </li>
                <li>
                  <strong className="font-semibold text-white">You are in control.</strong> Mute,
                  hide your camera or leave at any moment — the camera light goes out with you.
                </li>
              </ul>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/5 px-5 py-8 text-sm text-white/40 sm:px-8">
        <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-4 sm:flex-row sm:justify-between">
          <span>
            Made with <span className="text-phidio-500">♥</span> in Amsterdam
          </span>
          <nav className="flex items-center gap-5">
            <a
              className="transition hover:text-white"
              href="https://www.facebook.com/phidio"
              target="_blank"
              rel="noreferrer"
            >
              Facebook
            </a>
            <a
              className="transition hover:text-white"
              href="https://twitter.com/phidioapp"
              target="_blank"
              rel="noreferrer"
            >
              X
            </a>
            <a
              className="transition hover:text-white"
              href="https://instagram.com/phidioapp"
              target="_blank"
              rel="noreferrer"
            >
              Instagram
            </a>
            <a
              className="transition hover:text-white"
              href="https://github.com/phidio"
              target="_blank"
              rel="noreferrer"
            >
              GitHub
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="glass rounded-2xl px-4 py-5 text-center">
      <div
        className={`text-2xl font-semibold tabular-nums sm:text-3xl ${
          accent ? 'text-phidio-400' : 'text-white'
        }`}
      >
        {value}
      </div>
      <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-white/40">{label}</div>
    </div>
  );
}

function Feature({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="glass rounded-3xl p-6">
      <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-phidio-500/15 text-phidio-400">
        {icon}
      </span>
      <h3 className="mt-4 text-lg font-semibold">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-white/55">{body}</p>
    </div>
  );
}
